/**
 * Form data parsing utilities.
 *
 * Convert FormData objects to typed structures matching our schemas.
 * All functions are SSR-safe and can be used in server actions.
 *
 * @module utils/parse-form-data
 */

/**
 * Parse FormData from a care seeker form into a structured object.
 *
 * This utility converts the flat FormData structure (with dot-notation keys
 * like "step1.role") into a nested object structure that matches the
 * CareSeekerFormData type.
 *
 * @param formData - The FormData object from form submission
 * @returns Structured form data object
 *
 * @example
 * ```ts
 * const formData = await request.formData();
 * const data = parseCareSeekerFormData(formData);
 * const validated = careSeekerFormSchemaBase.parse(data);
 * ```
 */
export function parseCareSeekerFormData(formData: FormData) {
  return {
    step1: {
      formVariant: (formData.get("step1.formVariant") || "v1") as "v1" | "v2",
      leadSource: formData.get("step1.leadSource") as string,
      role: formData.get("step1.role") || "care-seeker",
    },
    step2: {
      careFor: formData.get("step2.careFor") as string,
    },
    step3: {
      everydayTasks: formData.get("step3.everydayTasks") === "on",
      memoryCare: formData.get("step3.memoryCare") === "on",
      mobilityAssistance: formData.get("step3.mobilityAssistance") === "on",
      other: formData.get("step3.other") === "on",
    },
    step4: {
      location: formData.get("step4.location") as string,
      organizationNumber: formData.get("step4.organizationNumber")
        ? Number(formData.get("step4.organizationNumber"))
        : undefined,
      postalCode:
        (formData.get("step4.postalCode") as string | undefined) || undefined,
      urgency:
        (formData.get("step4.urgency") as string | undefined) || undefined,
    },
    step5: {
      consentTerms: formData.get("step5.consentTerms") === "on",
      email: formData.get("step5.email") as string,
      firstName: formData.get("step5.firstName") as string,
      lastName: formData.get("step5.lastName") as string,
      OptInEmail: true,
      OptInSms: formData.get("step5.OptInSms") === "on",
      phone: formData.get("step5.phone") as string,
    },
  };
}
